import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import Tabs, { type Tab } from '../components/common/Tabs';
import StatusBadge from '../components/common/StatusBadge';
import EdgeActionButtons from './edges/health/EdgeActionButtons';
import PhysicalInterfacesConfig from './edges/config/PhysicalInterfacesConfig';
import LogicalInterfacesConfig from './edges/config/LogicalInterfacesConfig';
import Subscriptions from './edges/config/Subscriptions';
import StaticRoutes from './edges/config/StaticRoutes';
import ACLConfig from './edges/config/ACLConfig';
// import QoSConfig from './edges/config/QoSConfig'; // Hidden - covered by Routing Policies
import WireGuard from './edges/config/WireGuard';
import FlowExportSettings from './edges/monitoring/FlowExportSettings';
import SuricataSettings from './edges/monitoring/SuricataSettings';
import RoutingPoliciesConfig from './edges/config/RoutingPoliciesConfig';
import SSHAccess from '../components/edges/SSHAccess';
import KeyRotationManagement from '../components/edges/KeyRotationManagement';
import ConfigHistory from '../components/edges/ConfigHistory';
import SyncConfigButton from '../components/edges/SyncConfigButton';
import { Server, Network, Layers, GitBranch, Shield, Key, BarChart3, Terminal, RefreshCw, Route } from 'lucide-react';
import { edgeService, observabilityService } from '../services';
import type { Edge } from '../services/edge.service';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../stores/authStore';
import { USER_TYPES } from '../utils/constants';

/**
 * Determine if an edge is online based on VictoriaMetrics telemetry data.
 * An edge is online if it has a CPU metric — this means VictoriaMetrics
 * received fresh observability data from the edge (published every 15s).
 */
const isEdgeOnline = (
  telemetry: { cpu?: number } | undefined | null,
): boolean => {
  return !!telemetry && telemetry.cpu !== undefined;
};

const EdgeConfig = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canUpdate } = usePermissions();
  // VSNP-964: SSH access (and the Key Rotation tab, which manages the same SSH
  // key material) is a Super-Admin-only capability. Gate on the concrete user
  // type — NOT usePermissions()/isSuperAdmin, which collapse Channel Admin
  // (type 5) into Super Admin. Channel Admin is excluded here, matching the
  // API guard's fail-closed decision. This mirrors the server enforcement in
  // vsm-api-v2 (edgeAuth.ts requireSuperAdmin); the tab hiding is cosmetic.
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  const [edge, setEdge] = useState<Edge | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [telemetry, setTelemetry] = useState<any>(null);
  // Hoisted active-tab state so the Subscriptions summary can programmatically
  // switch to the Logical Interfaces tab when the user clicks "Edit" on a row.
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'physical');

  useEffect(() => {
    const fetchEdge = async () => {
      if (!id) {
        setError('Edge ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await edgeService.getById(id);

        // Automatic redirection for legacy Snowflake ID URLs
        const isSnowflakeId = /^-?\d+$/.test(id);
        if (isSnowflakeId && data.devSerNum) {
          navigate(`/edges/${data.devSerNum}/config`, { replace: true });
          return;
        }

        setEdge(data);
        setError(null);
      } catch (err: any) {
        console.error('Failed to load edge:', err);
        setError(err.message || 'Failed to load edge details');
      } finally {
        setLoading(false);
      }
    };

    fetchEdge();
  }, [id, navigate]);

  useEffect(() => {
    if (edge?.devSerNum) {
      const guid = edge.devSerNum;

      const fetchTelemetry = async (isBackground = false) => {
        try {
          const options = isBackground ? { showLoading: false } : undefined;
          const res = await observabilityService.getEdgesTelemetry(options);
          if (res.status === 'success' && res.data[guid.toLowerCase()]) {
            setTelemetry(res.data[guid.toLowerCase()]);
          } else {
            // VSNP-700 follow-up: sentinel empty object distinguishes
            // "fetched, no signal" (= offline) from "still loading"
            // (= telemetry is null). Used by the offline banner above
            // the tab group so it only shows after we've actually tried.
            setTelemetry((prev: any) => prev ?? {});
          }
        } catch (err) {
          console.error('Failed to fetch telemetry for config header:', err);
          setTelemetry((prev: any) => prev ?? {});
        }
      };

      fetchTelemetry();
      const interval = setInterval(() => fetchTelemetry(true), 30000);
      return () => clearInterval(interval);
    }
  }, [edge?.devSerNum]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading edge configuration...</div>
      </div>
    );
  }

  if (error || !edge) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error || 'Edge not found'}</div>
      </div>
    );
  }

  const tabs: Tab[] = [
    { id: 'physical', label: 'Physical Interfaces', icon: Server, content: <PhysicalInterfacesConfig edgeId={edge.devSerNum} /> },
    { id: 'logical', label: 'Logical Interfaces', icon: Network, content: <LogicalInterfacesConfig edgeId={edge.devSerNum} /> },
    { id: 'subscriptions', label: 'Subscriptions', icon: Layers, content: <Subscriptions edgeId={edge.devSerNum} onNavigateToLogicalInterfaces={() => setActiveTab('logical')} /> },
    { id: 'static-routes', label: 'Static Routes', icon: GitBranch, content: <StaticRoutes edgeId={edge.devSerNum} /> },
    { id: 'acl', label: 'ACL', icon: Shield, content: <ACLConfig /> },
    { id: 'routing-policies', label: 'Policies', icon: Route, content: <RoutingPoliciesConfig edgeId={edge.devSerNum} tenantId={edge.tenantId} /> },
    // QoS tab hidden - functionality covered by Routing Policies and Service Chain
    // { id: 'qos', label: 'QoS', icon: Gauge, content: <QoSConfig /> },
    { id: 'gateways', label: 'Gateways', icon: Key, content: <WireGuard /> },
    { id: 'flow-export', label: 'Flow Export', icon: BarChart3, content: <FlowExportSettings edgeId={edge.devSerNum} /> },
    { id: 'suricata', label: 'Suricata IDS/IPS', icon: Shield, content: <SuricataSettings edgeId={edge.devSerNum} /> },
    // VSNP-964: SSH Access + Key Rotation are Super-Admin-only.
    ...(isSuperAdmin ? [
      { id: 'ssh-access', label: 'SSH Access', icon: Terminal, content: <SSHAccess edgeGuid={edge.devSerNum} edgeStatus={edge.upInformation ? 'provisioned' : 'unknown'} /> },
      { id: 'key-rotation', label: 'Key Rotation', icon: RefreshCw, content: <KeyRotationManagement edgeGuid={edge.devSerNum} /> },
    ] : []),
  ];

  // VSNP-964 / VSNP-960 fallback: a deep link like ?tab=ssh-access must not
  // render a blank pane when the requested tab was filtered out for this user.
  // Fall back to the first available tab if the active tab isn't present.
  const effectiveActiveTab = tabs.some((t) => t.id === activeTab)
    ? activeTab
    : (tabs[0]?.id ?? '');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/edges/${id}`)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Edge Configuration</h1>
            <div className="flex items-center space-x-3 mt-2">
              <p className="text-gray-600">{edge.devName} • {edge.devLocation || 'Unknown Location'}</p>
              <StatusBadge status={isEdgeOnline(telemetry) ? 'Online' : 'Offline'} />
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <ConfigHistory edgeId={edge.devSerNum} />
          <SyncConfigButton edgeId={edge.devSerNum} />
          {canUpdate && (
            <EdgeActionButtons
              edgeOnline={isEdgeOnline(telemetry)}
              edgeId={edge.devSerNum}
            />
          )}
        </div>
      </div>

      {/* VSNP-700 follow-up: compact-but-prominent offline notice above
          the tab group so admins editing an unreachable edge know what
          they're looking at. The telemetry !== null guard prevents a
          flash during initial page load. */}
      {telemetry !== null && !isEdgeOnline(telemetry) && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
          <span>
            <span className="font-semibold">Edge offline.</span>{' '}
            You're editing the orchestrator-stored configuration. Changes save here and will reach the edge on its next sync after it reconnects.
          </span>
        </div>
      )}

      {/* Configuration Tabs */}
      <div className="card">
        <Tabs tabs={tabs} activeTab={effectiveActiveTab} onChange={setActiveTab} />
      </div>
    </div>
  );
};

export default EdgeConfig;

